import React, { useEffect, useState } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  ListItemIcon,
} from '@mui/material'
import MuiDialogTitle from '@mui/material/DialogTitle'
import CloseIcon from '@mui/icons-material/Close'
import { ArrowForwardIos } from '@mui/icons-material'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import { useIntl } from 'react-intl'
import LinkIcon from '@mui/icons-material/Link'

const PREFIX = 'PopupDialogComponent'

const classes = {
  root: `${PREFIX}-root`,
  dialogPaper: `${PREFIX}-dialogPaper`,
}

const StyledDialog = styled(Dialog)(() => ({
  [`& .${classes.root}`]: {
    margin: 0,
  },
  [`& .${classes.dialogPaper}`]: {
    maxWidth: '100%',
  },
}))

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
})

function ListItemLink(props) {
  return <ListItem button component="a" {...props} />
}

const DialogTitle = (props) => {
  const { children, onClose, users_reached = '', created_at = null, ...other } = props
  const intl = useIntl()
  const { formatMessage } = intl
  const theme = useTheme()
  const componentStyles = styles(theme)

  return (
    <MuiDialogTitle disableTypography sx={componentStyles.root} {...other}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '10px' }}>
        <div>
          <Typography
            variant="h6"
            align="center"
            color="primary"
            style={{ marginTop: 8, fontSize: '1rem' }}
          >
            <i>{children}</i>
          </Typography>
          {onClose ? (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={componentStyles.closeButton}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
        {users_reached ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              marginTop: 10,
            }}
          >
            <Tooltip title="Usuarios alcanzados">
              <Typography
                variant="body1"
                color="textSecondary"
                display="inline"
                style={{ marginLeft: '10px' }}
              >
                {users_reached}
              </Typography>
            </Tooltip>
            <Typography
              variant="body1"
              color="textSecondary"
              display="inline"
              style={{ paddingRight: '20px' }}
            >
              {formatMessage({ id: 'pages.notices.popup.created.title' })}{' '}
              {new Date(created_at).toLocaleString().substring(0, 10)}
            </Typography>
          </div>
        ) : null}
      </div>
    </MuiDialogTitle>
  )
}

export const PopupDialogComponent = ({
  id = '',
  title = '',
  text = '',
  links = [],
  onRead = () => {},
  onClose = () => {},
  onPrev = () => {},
  onNext = () => {},
  open = false,
  quantity = 0,
  position = 1,
  ignore = false,
  customButtons = [],
  showCheckbox = true,
}) => {
  const [checked, setChecked] = useState(ignore)
  const intl = useIntl()
  const { formatMessage } = intl

  useEffect(() => {
    setChecked(ignore)
  }, [id, open])

  function handleRead() {
    setChecked(true)
    onRead()
  }

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth={'md'}
    >
      {quantity ? (
        <DialogTitle onClose={onClose}>
          {formatMessage({ id: 'pages.notices.popup.new_notice.title' })} ({quantity})
        </DialogTitle>
      ) : (
        <DialogTitle onClose={onClose}></DialogTitle>
      )}
      <DialogContent
        style={{ padding: '0.5rem 3rem', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}
      >
        <h1
          style={{
            marginTop: 0,
            textAlign: 'center',
            textTransform: 'uppercase',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            paddingBottom: '0.5rem',
          }}
        >
          {title}
        </h1>
        <div
          dangerouslySetInnerHTML={{ __html: text }}
          style={{ color: '#63666A', textAlign: 'justify', marginTop: '1rem' }}
        ></div>
        {links && links.length > 0 && (
          <>
            <Typography
              variant="h6"
              style={{ marginTop: '2rem', marginBottom: '0.5rem' }}
              color="primary"
            >
              Enlaces Adjuntos <i>({links.length})</i>
            </Typography>
            <List>
              {links.map((link) => (
                <ListItemLink href={link.key} target="_blank" key={link.key}>
                  <ListItemIcon color="primary">
                    <LinkIcon />
                  </ListItemIcon>
                  <ListItemText primary={link.value} secondary={link.key} />
                </ListItemLink>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            padding: '0 20px',
          }}
        >
          {showCheckbox && (
            <FormControlLabel
              style={{ marginLeft: '5px' }}
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleRead}
                  name="checkedB"
                  color="primary"
                  disabled={checked}
                />
              }
              label={formatMessage({ id: 'pages.notices.popup.check_as_read.title' })}
            />
          )}

          {customButtons.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'center',
              }}
            >
              {customButtons.map((button, index) => (
                <Button
                  key={index}
                  variant={button.variant || 'contained'}
                  color={button.color || 'primary'}
                  onClick={button.onClick}
                  style={button.style || {}}
                >
                  {button.text}
                </Button>
              ))}
            </div>
          )}

          {quantity ? (
            <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
              <IconButton
                aria-label="previous"
                disabled={position === 1}
                onClick={onPrev}
                size="large"
              >
                <ArrowBackIos />
              </IconButton>
              <Typography variant="body1" color="primary" display="inline">
                <i>
                  {position} {formatMessage({ id: 'pages.notices.popup.de.text' })} {quantity}
                </i>
              </Typography>
              <IconButton
                aria-label="next"
                disabled={position === quantity}
                onClick={onNext}
                size="large"
              >
                <ArrowForwardIos />
              </IconButton>
            </div>
          ) : null}
        </div>
      </DialogActions>
    </StyledDialog>
  )
}
